import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import Loader from "../../../common/Loader";
import ImageComponent from '../../../../components/common/Image';
import QRScanner from '../../../common/Scanner/QRScanner';
import Lightbox from "../../../common/LightBox";
import { productDetails, imeiValidation, updateImeiDetails, getImeiDetails, setStage, setSubStage, setImeiNumber, setProductCost } from '../../../../slices/eligibility/eligibilitySlice';
import { toTitleCaseAdvanced } from '../../../../utils/functions';
import Modal, { ModalBody, ModalHeader } from '../../../common/Modal';
import Screenbros from './Screenbros';

function SerialNumberValidation() {

    const urlParams = new URLSearchParams(window.location.search);
    const referenceId = urlParams.get('referenceId');
    const { applicationData, productDetailsData, isLoading, subStage } = useSelector(state => state.eligibility);
    const dispatch = useDispatch();

    const [loader, setLoader] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [scanSuccess, setScanSuccess] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [showValidationSuccessModal, setShowValidationSuccessModal] = useState(false);
    const [showImeiConfirmModal, setShowImeiConfirmModal] = useState(false);
    const [showInitiateButton, setShowInitiateButton] = useState(false);
    const [showCheckStatusButton, setShowCheckStatusButton] = useState(false);
    const [isImeiRegistered, setIsImeiRegistered] = useState(false);
    const [screenBros, setScreenBros] = useState();
    const [overlay, setOverlay] = useState(false);
    const [serialNumber, setSerialNumber] = useState('');
    const [screenBrosScreen, setScreenBrosScreen] = useState(false)

    // Call productDetails API on component load
    useEffect(() => {
        if (referenceId) {
            dispatch(productDetails({ reference_id: referenceId }));
        }
    }, [dispatch, referenceId]);

    useEffect(() => {
        setSerialNumber(productDetailsData?.response_data?.imei_number)
    }, [productDetailsData?.response_data?.imei_number])

    // Update button visibility based on product type and validation status
    useEffect(() => {
        const responseData = productDetailsData?.response_data || {};
        const product = responseData.product;
        const category = responseData.category;

        if (isValidated) {
            if (product === 'DPL' && (category.toLowerCase() === 'mobile phone' || category.toLowerCase() === 'tablet')) {
                setShowInitiateButton(true);
                setShowCheckStatusButton(false);
            } else {
                setShowInitiateButton(false);
                setShowCheckStatusButton(false);
            }
        } else {
            setShowInitiateButton(false);
            setShowCheckStatusButton(false);
        }
    }, [productDetailsData, isValidated]);

    const handleScan = (scannedData) => {
        setSerialNumber(scannedData);
        setShowScanner(false);
        setScanSuccess(true);
        setShowImeiConfirmModal(true);
        setTimeout(() => setScanSuccess(false), 3000);
    };

    const handleManualInput = (e) => {
        const value = e.target.value;
        // keep only alphanumeric and trim to 24 chars
        const sanitized = value.slice(0, 32);
        setSerialNumber(sanitized);
        setScanSuccess(false);
    };

    const handleInitiate = async () => {
        setLoader(true);

        try {
            const responseData = productDetailsData?.response_data || {};

            const updatePayload = {
                application_id: referenceId,
                imei1: serialNumber || "",
                imei2: "",
                brand: responseData.brand || "",
                model: responseData.model || "",
                status: "register"
            };

            const updateResult = await dispatch(updateImeiDetails(updatePayload));

            if (updateResult.payload?.response_code === 'S100') {
                // Hide Initiate button and show Check Status button
                setShowInitiateButton(false);
                setShowCheckStatusButton(true);
                setScreenBros(false)

                // Open the URL from response_data if available
                setScreenBrosScreen(true)
            } else {
                toast.error(updateResult.payload?.response_string);
            }
        } catch (error) {
            toast.error('An error occurred while initiating IMEI registration');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (screenBros) {
            handleCheckStatus()
        }
    }, [screenBros])

    function waitForClose(win, cb) {
        function poll() {
            if (win.closed) {
                setScreenBros(true)
            }
            else {
                requestAnimationFrame(poll);
            }
        }
        poll();
    }

    const handleCheckStatus = async () => {
        setLoader(true);

        try {
            const getImeiPayload = {
                application_id: referenceId,
                imei1: serialNumber
            };

            const getImeiResult = await dispatch(getImeiDetails(getImeiPayload));

            if (getImeiResult.payload?.response_code === 'S100' &&
                getImeiResult.payload?.response_data?.status === 'success' &&
                getImeiResult.payload?.response_data?.status_code === 2000) {

                setIsImeiRegistered(true);
                // toast.success('IMEI registration completed successfully. You can now proceed.');
                return;
            } else {
                // Call updateImeiDetails api again
                const responseData = productDetailsData?.response_data || {};

                const updatePayload = {
                    application_id: referenceId,
                    imei1: serialNumber || "",
                    imei2: "",
                    brand: responseData.brand || "",
                    model: responseData.model || "",
                    status: "register"
                };

                const updateResult = await dispatch(updateImeiDetails(updatePayload));

                if (updateResult.payload?.response_code === 'S100') {
                    setScreenBrosScreen(true)
                } else {
                    toast.error(updateResult.payload?.response_string);
                }
            }
        } catch (error) {
            toast.error('An error occurred while checking IMEI status');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (applicationData?.screen_bros_validation === 'success') {
            setScreenBrosScreen(false)
            setIsImeiRegistered(true);
            setShowCheckStatusButton(false);

        }
    }, [applicationData?.screen_bros_validation])

    const handleProceed = async () => {
        if (!isValidated) {
            toast.error('Please validate the serial number before proceeding');
            return;
        }

        const responseData = productDetailsData?.response_data || {};
        const product = responseData.product;
        const category = responseData.category;

        if ((product !== 'DPL') || (category.toLowerCase() !== 'mobile phone' || category.toLowerCase() !== 'tablet')) {
            // For non-DPL products, directly proceed to invoice details
            dispatch(setStage("invoice_details"));
            dispatch(setSubStage("invoice_details"));
            dispatch(setImeiNumber(serialNumber));
            dispatch(setProductCost(responseData?.product_cost));
        } else {
            // For DPL products, check if IMEI is registered
            if (isImeiRegistered) {
                dispatch(setStage("invoice_details"));
                dispatch(setSubStage("invoice_details"));
                dispatch(setImeiNumber(serialNumber));
                dispatch(setProductCost(responseData?.product_cost));
            } else {
                toast.error('Please complete IMEI registration process first');
            }
        }
    };

    const handleValidateSerialNumber = async () => {
        setLoader(true);

        const responseData = productDetailsData?.response_data || {};
        const payload = {
            reference_id: referenceId,
            oem_brand: responseData.brand || '',
            category: responseData.category || '',
            model: responseData.model || '',
            variant: responseData.model || '',
            imei: serialNumber?.trim(),
            dms_code: responseData.dms_code || '',
            state_code: responseData.state_code || '',
            product: responseData.product || '',
            request_type: 'block'
        };

        try {
            const result = await dispatch(imeiValidation(payload));

            if (result.payload?.response_code === 'S100') {
                setShowValidationSuccessModal(true);
                setIsValidated(true);
                setTimeout(() => {
                    setShowValidationSuccessModal(false);
                }, 5000);
            } else {
                toast.error(result.payload?.response_string);
                setIsValidated(false);
            }
        } catch (error) {
            toast.error('An error occurred during validation', error);
            setIsValidated(false);
        } finally {
            setLoader(false);
        }
    };

    const handleImeiConfirmNo = () => {
        setSerialNumber(''); // Reset the serial number input field
        setShowImeiConfirmModal(false);
        setScanSuccess(false);
    };

    const handleImeiConfirmYes = () => {
        setShowImeiConfirmModal(false); // Just close the modal
    };

    const handleImeiRetrieve = async () => {
        try {
            setOverlay(false);
            setLoader(true);

            const getImeiPayload = {
                application_id: referenceId,
                imei1: serialNumber
            };

            const getImeiResult = await dispatch(getImeiDetails(getImeiPayload));

            if (getImeiResult.payload?.response_code === 'S100' &&
                getImeiResult.payload?.response_data?.status === 'success' &&
                getImeiResult.payload?.response_data?.status_code === 2000) {

                setIsImeiRegistered(true);
                setShowCheckStatusButton(false);
                // toast.success('Device registration completed successfully. You can now proceed.');
            } else {
                toast.info('Device registration is still in progress. Please complete the process and try again.');
                setOverlay(true);
            }
        } catch (error) {
            console.error('Error retrieving device registration status:', error);
            toast.error('Failed to retrieve device registration status');
            setOverlay(true);
        } finally {
            setLoader(false);
        }
    };


    // Extract product details from API response
    const responseData = productDetailsData?.response_data || {};
    const {
        brand = '',
        category = '',
        model = '',
        dms_code = '',
        state_code = '',
        product = '',
        description = ''
    } = responseData;

    const isDPLProduct = product === 'DPL' && (category.toLowerCase() === 'mobile phone' || category.toLowerCase() === 'tablet');
    // const isDPLProduct = false

    return (
        <>
            {screenBrosScreen === true && createPortal(<Screenbros application_id={referenceId} brand={responseData?.brand} screenBrosScreen={screenBrosScreen} model={responseData?.model} serialNumber={serialNumber} />, document.body)}
            {overlay === true && createPortal(<Lightbox stage="imei_validation" onRetrieve={handleImeiRetrieve} />, document.body)}
            <div className="col-md-9 col-sm-12 Side-bank-Right custom-lr-padding">
                <div className="row gy-4 static-tab">
                    {scanSuccess && (
                        <div className="col-12">
                            <div className="alert alert-success alert-dismissible fade show">
                                <i className="fas fa-check-circle me-2"></i>
                                Code scanned successfully!
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setScanSuccess(false)}
                                ></button>
                            </div>
                        </div>
                    )}
                    <div className="col-12">
                        <div className="overall-tsmi">
                            <div className="accordion-button tS-imei">
                                <span className="fw-semibold">PRODUCT DETAILS</span>
                            </div>
                            <div className="p-3">
                                <p className="text-secondary d-flex justify-content-between small">
                                    Brand
                                    <span className="dark-blue fw-semibold text-end w-50">{brand || 'N/A'}</span>
                                </p>
                                <p className="text-secondary d-flex justify-content-between small">
                                    Category
                                    <span className="dark-blue fw-semibold text-end w-50">{category || 'N/A'}</span>
                                </p>
                                <p className="text-secondary d-flex justify-content-between small">
                                    Model
                                    <span className="dark-blue fw-semibold text-end w-50">{(description) || 'N/A'}</span>
                                </p>
                                {dms_code && <p className="text-secondary d-flex justify-content-between small">
                                    DMS Code
                                    <span className="dark-blue fw-semibold text-end w-50">{dms_code || 'N/A'}</span>
                                </p>}
                                {state_code && <p className="text-secondary d-flex justify-content-between small">
                                    State Code
                                    <span className="dark-blue fw-semibold text-end w-50">{state_code || 'N/A'}</span>
                                </p>}
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="input-container mt-3 has-icon">
                            <input
                                className={`form-control input-underline bg-transparent ${scanSuccess ? 'border-success' : ''}`}
                                type="text"
                                placeholder=""
                                value={serialNumber}
                                onChange={handleManualInput}
                                disabled={productDetailsData?.response_data?.imei_number || isValidated}
                                required
                            />
                            <label className="label form-label">Enter or Scan {(productDetailsData?.response_data?.category?.toLowerCase() === 'mobile phone' || productDetailsData?.response_data?.category?.toLowerCase() === 'tablet') ? 'IMEI' : `Serial`} Number ({(productDetailsData?.response_data?.category?.toLowerCase() === 'mobile phone' || productDetailsData?.response_data?.category?.toLowerCase() === 'tablet') && 'IMEI 1'})</label>
                            <div className="underline"></div>

                            <div className="">
                                {(!isValidated && !productDetailsData?.response_data?.imei_number) ? (
                                    <div
                                        className="barcode-wrapper"
                                        onClick={() => setShowScanner(true)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setShowScanner(true);
                                            }
                                        }}
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Click to scan barcode/QR code"
                                        title="Click to scan barcode/QR code"
                                    >
                                        <ImageComponent
                                            fileName="barcode.svg"
                                            alt="barcode"
                                            className="barcode"
                                        />
                                    </div>
                                ) : (
                                    <div className="validation-success-icon">
                                        <i className="fs-4 icon-Essetional-green-Icon">
                                            <span className="verified">Verified</span>
                                            <span className="path1"></span>
                                            <span className="path2"></span>
                                        </i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {(!isValidated && subStage !== 'screen_bros') && (
                        <div className="col-12">
                            <button
                                className="btn btn-primary btn-lg w-100 signin"
                                type="button"
                                disabled={isLoading || !serialNumber?.trim()}
                                onClick={handleValidateSerialNumber}
                            >
                                {isLoading ? 'Validating...' : `Validate ${(productDetailsData?.response_data?.category?.toLowerCase() === 'mobile phone' || productDetailsData?.response_data?.category?.toLowerCase() === 'tablet') && `IMEI` || `Serial`} Number`}
                            </button>
                        </div>
                    )}

                    {/* New buttons for DPL products */}
                    {((isValidated || subStage === 'screen_bros')) && isDPLProduct && (
                        <>
                            {(showInitiateButton || subStage === 'screen_bros') && (
                                <div className="col-12">
                                    <button
                                        className="btn btn-primary btn-lg w-100 signin"
                                        type="button"
                                        disabled={loader}
                                        onClick={handleInitiate}
                                    >
                                        {loader ? 'Initiating...' : `Initiate ${(productDetailsData?.response_data?.category?.toLowerCase() === 'mobile phone' || productDetailsData?.response_data?.category?.toLowerCase() === 'tablet') && `device set-up`}`}
                                    </button>
                                </div>
                            )}
                            {showCheckStatusButton && (
                                <div className="col-12 d-none">
                                    <button
                                        className="btn btn-primary btn-lg w-100 signin"
                                        type="button"
                                        disabled={loader}
                                        onClick={handleCheckStatus}
                                    >
                                        {loader ? 'Checking Status...' : 'Check Status'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <footer className="sticky-bottom-1">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="d-flex justify-content-end">
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    disabled={
                                        isLoading ||
                                        loader ||
                                        !isValidated ||
                                        (isDPLProduct && !showCheckStatusButton)
                                    }
                                    onClick={handleProceed}
                                >
                                    {isLoading || loader ? 'Processing...' : 'Proceed'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>

            <QRScanner
                isOpen={showScanner}
                onScan={handleScan}
                onClose={() => setShowScanner(false)}
            />

            <Modal
                id="successModal"
                show={showValidationSuccessModal}
                onClose={() => setShowValidationSuccessModal(false)}
                modalClass="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>
                <ModalBody className="mt-3 mb-3">
                    <div className="px-3 py-3 text-center">
                        <ImageComponent fileName="new-congratulations.gif" className="gif-tick" />
                        <p className="fw-semibold m-0 mt-3">{(productDetailsData?.response_data?.category?.toLowerCase() === 'mobile phone' || productDetailsData?.response_data?.category?.toLowerCase() === 'tablet') ? `IMEI` : `Serial`} Number Validated</p>
                        <p className="text-success fw-semibold">Successfully</p>
                    </div>
                </ModalBody>
            </Modal>

            <Modal
                id="imeiConfirmModal"
                show={showImeiConfirmModal}
                onClose={() => setShowImeiConfirmModal(false)}
                modalClass="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal custom-modal"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>
                <ModalBody className="imei-popup">
                    <div className="text-center">
                        <ImageComponent fileName="serial.png" className="store-image" width='232px' />
                        <p className="fw-semibold">Please Confirm the Serial / IMEI Number</p>
                        <p className="text-primary fw-semibold">{serialNumber}</p>
                        <div className="d-flex gap-4 justify-content-center">
                            <button
                                className="btn btn-outline-primary flex-fill"
                                type="button"
                                onClick={handleImeiConfirmNo}
                            >
                                No
                            </button>
                            <button
                                className="btn btn-primary btn-lg signin flex-fill"
                                type="button"
                                onClick={handleImeiConfirmYes}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            {(loader) && <Loader />}
        </>
    )
}

export default SerialNumberValidation;